import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AutoHideScrollArea } from '@/components/AutoHideScrollArea';
import { EmptyState } from '@/components/EmptyState';
import { ChevronLeft } from '@/components/Icons/ChevronLeft';
import { ChevronRight } from '@/components/Icons/ChevronRight';
import { PageSpinner } from '@/components/Spinner/PageSpinner';
import { useHasFamilySubscription } from '@/hooks/useHasFamilySubscription';
import { useIsMobile } from '@/hooks/useIsMobile';
import { JournalDetails } from '@/pages/Overview/JournalDetails';
import { JournalList } from '@/pages/Overview/JournalList';
import { JournalMobileDetails } from '@/pages/Overview/JournalMobileDetails';
import { JournalMobileList } from '@/pages/Overview/JournalMobileList';
import { JournalPaywall } from '@/pages/Overview/JournalPaywall';
import { PeriodPicker } from '@/pages/Overview/PeriodPicker';
import { useJournals } from '@/pages/Overview/hooks/useJournals';
import { useSelectedPeriod } from '@/pages/Overview/hooks/useSelectedPeriod';
import type { Journal } from '@/types/journal';
import { getJournalKey, getTeacherNames } from '@/utils/journal';
import { tv } from '@/utils/tv';

const collapseButton = tv({
  base: 'mt-6 hidden size-8 shrink-0 cursor-pointer items-center justify-center rounded-full bg-card text-muted-foreground shadow-card transition-all duration-300 ease-out hover:bg-hover md:flex',
  variants: {
    isCollapsed: {
      true: 'md:-ml-3',
      false: '',
    },
  },
});

const listPanel = tv({
  base: 'flex overflow-hidden rounded-[20px] bg-card shadow-card transition-all duration-300 ease-out md:min-h-0 md:w-[257px]',
  variants: {
    isCollapsed: {
      true: 'md:-mr-3 md:w-0 md:opacity-0',
      false: 'md:opacity-100',
    },
  },
});

interface Props {
  onOpenChange: (isOpen: boolean) => void;
}

export const Journals: React.FC<Props> = ({ onOpenChange }) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const hasSubscription = useHasFamilySubscription();
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [isListCollapsed, setIsListCollapsed] = useState(false);
  const { data: journals, isLoading } = useJournals();
  const { periods, selectedPeriod, selectPeriod } = useSelectedPeriod();

  const openedJournal = journals?.find(
    (journal) => getJournalKey(journal.journalId) === selectedKey,
  );
  const selectedJournal = isMobile
    ? openedJournal
    : (openedJournal ?? journals?.[0]);

  if (isLoading) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <PageSpinner className="size-[22px] text-subtle-foreground" />
      </div>
    );
  }

  if (!journals?.length) {
    return <EmptyState label={t('overview.journals.empty')} />;
  }

  const openJournal = (journal: Journal) => {
    setSelectedKey(getJournalKey(journal.journalId));
    onOpenChange(true);
  };

  const closeJournal = () => {
    setSelectedKey(null);
    onOpenChange(false);
  };

  if (isMobile && !selectedJournal) {
    return <JournalMobileList journals={journals} onSelect={openJournal} />;
  }

  if (isMobile && selectedJournal) {
    return (
      <JournalMobileDetails
        journal={selectedJournal}
        periods={periods}
        selectedPeriod={selectedPeriod}
        onPeriodChange={selectPeriod}
        onBack={closeJournal}
      />
    );
  }

  return (
    <div className="flex flex-1 flex-col gap-3 px-4 pt-1 pb-6 md:min-h-0 md:flex-row md:px-6 md:pt-6 md:pb-5">
      <div className={listPanel({ isCollapsed: isListCollapsed })}>
        <JournalList
          journals={journals}
          selectedJournal={selectedJournal}
          onSelect={(journal) =>
            setSelectedKey(getJournalKey(journal.journalId))
          }
        />
      </div>

      <button
        type="button"
        onClick={() => setIsListCollapsed(!isListCollapsed)}
        className={collapseButton({ isCollapsed: isListCollapsed })}
      >
        {isListCollapsed && <ChevronRight className="size-4" />}
        {!isListCollapsed && <ChevronLeft className="size-4" />}
      </button>

      {!!selectedJournal && !hasSubscription && (
        <AutoHideScrollArea className="hidden min-h-0 min-w-0 flex-1 md:flex md:flex-col">
          <JournalPaywall journal={selectedJournal} />
        </AutoHideScrollArea>
      )}

      {!!selectedJournal && hasSubscription && (
        <div className="hidden min-h-0 min-w-0 flex-1 flex-col rounded-[20px] bg-card shadow-card md:flex">
          <div className="relative z-10 flex shrink-0 items-start justify-between gap-4 px-5 pt-6 pb-4">
            <div className="flex min-w-0 flex-col gap-3">
              <h1 className="text-title-24 truncate text-foreground">
                {selectedJournal.name}
              </h1>
              <p className="text-body-14 min-h-5 truncate text-subtle-foreground">
                {getTeacherNames(selectedJournal)}
              </p>
            </div>
            <PeriodPicker
              isAlignedRight
              periods={periods}
              value={selectedPeriod}
              onChange={selectPeriod}
            />
          </div>

          <AutoHideScrollArea
            hasTopShadow
            className="isolate flex min-h-0 flex-1 flex-col"
            viewportClassName="[&>div]:h-full"
          >
            <div className="flex min-h-full flex-col px-5 pb-8">
              <JournalDetails
                journalId={selectedJournal.journalId}
                period={selectedPeriod}
              />
            </div>
          </AutoHideScrollArea>
        </div>
      )}
    </div>
  );
};
